import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { BiRightArrowAlt, BiTrash } from "react-icons/bi";
import { toast } from "react-toastify";
import DisplayDate from "components/DisplayDate";
import { removeClientFromFeedback } from "utils/followUpService";

const FollowUpTable = ({
  refreshTrigger,
  onRowClick,
  selectedClients,
  feedbackClients,
  isFeedbackClientsLoading,
  feedbackClientsError,
  hasPermissionToFollowUpAdmin,
}) => {
  const [clients, setClients] = useState([]);
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  const removeClientFromFeedbackMutation = useMutation({
    mutationFn: removeClientFromFeedback,
    onSuccess: () => {
      toast.success("Client removed successfully");
      queryClient.invalidateQueries({ queryKey: ["feedbackClientsTable"] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error removing client");
    },
  });

  useEffect(() => {
    if (feedbackClients) {
      setClients(feedbackClients);
    }
  }, [feedbackClients]);

  const handleRemoveClient = async (e, clientId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to remove this client?")) {
      removeClientFromFeedbackMutation.mutate(clientId);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "client.name",
        header: "Client",
        size: 220,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium text-app-text truncate">{row.original?.client?.name}</div>
            {row.original?.client?.passportNumber ? (
              <div className="text-app-text-muted text-xs mt-0.5 truncate">
                {row.original.client.passportNumber}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "client.email",
        header: "Email",
        size: 240,
        Cell: ({ row }) => (
          <span className="text-sm text-app-text-secondary">
            {row.original?.client?.email || "—"}
          </span>
        ),
      },
      {
        accessorKey: "client.number",
        header: "Phone",
        size: 140,
        Cell: ({ row }) => (
          <span className="text-sm text-app-text-secondary">
            {row.original?.client?.number || "—"}
          </span>
        ),
      },
      {
        accessorKey: "createdByName",
        header: "Created By",
        size: 150,
        Cell: ({ row }) => (
          <span className="text-sm text-app-text-muted">{row.original?.createdByName || "—"}</span>
        ),
      },
      {
        accessorKey: "createdDate",
        header: "Created",
        size: 140,
        Cell: ({ row }) => <DisplayDate value={row.original?.createdDate} />,
      },
      {
        accessorKey: "followUpDate",
        header: "Follow Up Date",
        size: 150,
        Cell: ({ row }) => <DisplayDate value={row.original?.followUpDate} />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1.5">
            <Tooltip title="Open feedback">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick(row.original?.client?.id, row.original?.id);
                }}
              >
                <BiRightArrowAlt size={18} />
              </IconButton>
            </Tooltip>
            {hasPermissionToFollowUpAdmin && (
              <Tooltip title="Remove from queue">
                <IconButton
                  size="small"
                  onClick={(e) => handleRemoveClient(e, row.original?.id)}
                  className="text-app-text-muted hover:text-red-600"
                >
                  <BiTrash size={18} className="text-red-600" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [onRowClick]
  );

  const table = useMaterialReactTable({
    columns,
    data: clients,
    enableRowNumbers: true,
    enableStickyHeader: true,
    enablePagination: true,
    paginationDisplayMode: "pages",
    muiTableContainerProps: {
      sx: { maxHeight: "70vh", minHeight: "70vh", overflowY: "auto" },
    },
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      pagination,
    },
    state: {
      isLoading: isFeedbackClientsLoading || removeClientFromFeedbackMutation.isPending,
      showProgressBars: isFeedbackClientsLoading || removeClientFromFeedbackMutation.isPending,
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value ?? "");
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          onRowClick(row.original?.client?.id, row.original?.id);
        }
      },
      sx: { cursor: "pointer" },
    }),
  });

  if (isFeedbackClientsLoading) {
    return (
      <div className="flex justify-center items-center py-16 rounded-xl border border-app-border/80 bg-app-surface">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (feedbackClientsError) {
    return (
      <div className="text-red-600 p-5 bg-red-50 rounded-xl border border-red-100 text-sm">
        Error loading clients. Please try again.
      </div>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="text-center py-16 rounded-xl border border-app-border/80 bg-app-surface">
        <p className="text-app-text-secondary font-medium">No clients in queue</p>
        <p className="text-app-text-muted text-sm mt-1">Add clients above to get started.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-app-border/80 shadow-sm bg-app-surface overflow-hidden">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default FollowUpTable;
